import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Inbox as InboxIcon, IndianRupee, Send, CheckCircle2, RefreshCcw, BadgeCheck,
  Paperclip, X as XIcon, FileText, XCircle,
} from 'lucide-react';
import { inboxApi, fmtRupee, fmtAgo } from '../lib/inboxApi';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

/**
 * QuoteInbox — single component used by both customer (`role='customer'`) and
 * vendor (`role='vendor'`) pages. The two-pane layout (thread list left,
 * conversation right) matches the WhatsApp/Gmail mental model that B2B
 * buyers and sellers already know.
 *
 * The only role-specific bits are the composer (vendors get a "Send Quotation"
 * structured form, buyers get "Accept Quote" / "Request Revision" buttons that
 * appear only when the last vendor message is a quotation) and the URL prefix
 * the inboxApi helper uses.
 */
const StatusBadge = ({ status }) => {
  const map = {
    open: { label: 'Open', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    quoted: { label: 'Quoted', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    accepted: { label: 'Accepted', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-200' },
    closed: { label: 'Closed', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  };
  const m = map[status] || map.open;
  return <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded-full px-1.5 py-0.5 ${m.cls}`}>{m.label}</span>;
};

// Rendered inside a MessageBubble to show any file attachments as clickable chips.
const AttachmentChips = ({ attachments }) => {
  if (!attachments?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="msg-attachments">
      {attachments.map((a, i) => (
        <a
          key={a.url || `att-${i}`}
          href={a.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 max-w-[220px] text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 truncate"
          data-testid={`msg-attachment-${i}`}
          title={a.name}
        >
          <FileText className="w-3.5 h-3.5 shrink-0 text-[#2c54b2]" />
          <span className="truncate">{a.name}</span>
        </a>
      ))}
    </div>
  );
};

// Composer-level attachment picker + preview strip. Uses `inboxApi.uploadAttachment`
// to persist immediately (so refs survive across composer types) and hands back
// the full attachment descriptor arr for inclusion in the next post payload.
const AttachmentPicker = ({ role, threadId, disabled, attachments, setAttachments, testId = 'attach-btn' }) => {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking same file twice works
    if (!file) return;
    setBusy(true);
    try {
      const info = await inboxApi.uploadAttachment(role, threadId, file);
      setAttachments((prev) => [...prev, info]);
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testId}-wrap`}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || busy}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-[#ee5e23] border border-gray-300 rounded hover:border-[#ee5e23] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid={testId}
        title="Attach a PDF or image (max 10 MB)"
      >
        <Paperclip className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : 'Attach file'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
        onChange={onPick}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {attachments.map((a, i) => (
        <span key={a.url || `${a.name}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-[#2c54b2] rounded max-w-[160px] truncate" title={a.name}>
          <FileText className="w-3 h-3 shrink-0" />
          <span className="truncate">{a.name}</span>
          <button
            type="button"
            onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
            className="ml-0.5 hover:text-red-600"
            aria-label={`Remove ${a.name}`}
          >
            <XIcon className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
};

const QuotationCard = ({ q }) => (
  <div className="border border-amber-300 bg-amber-50 rounded-md p-3 mt-2" data-testid="quotation-card">
    <div className="flex items-center gap-1.5 text-amber-900 text-xs font-semibold uppercase tracking-wider mb-2">
      <IndianRupee className="w-3.5 h-3.5" /> Quotation
    </div>
    <div className="text-2xl font-bold text-amber-900">{fmtRupee(q?.price)}{q?.unit ? <span className="text-sm font-medium text-amber-800">/{q.unit}</span> : null}</div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-amber-900/80 mt-2">
      {q?.moq ? (<><span className="text-amber-900/60">MOQ</span><span className="font-medium">{q.moq}</span></>) : null}
      {q?.validity ? (<><span className="text-amber-900/60">Valid till</span><span className="font-medium">{q.validity}</span></>) : null}
      {q?.terms ? (<><span className="text-amber-900/60">Terms</span><span className="font-medium">{q.terms}</span></>) : null}
    </div>
  </div>
);

const ThreadList = ({ threads, role, activeId, onSelect }) => {
  if (!threads.length) {
    return (
      <div className="p-6 text-center text-sm text-gray-500" data-testid="inbox-empty">
        <InboxIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        No conversations yet. {role === 'customer' ? 'Use "Get Best Price" on any product or vendor.' : 'New buyer enquiries will show up here.'}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-gray-100" data-testid="inbox-thread-list">
      {threads.map((t) => {
        const isActive = t.id === activeId;
        const unread = role === 'customer' ? t.unread_for_customer : t.unread_for_vendor;
        const counterParty = role === 'customer' ? t.vendor_name : t.customer_name;
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onSelect(t)}
              data-testid={`inbox-thread-${t.id}`}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${isActive ? 'bg-blue-50/60 border-l-4 border-l-[#2c54b2] pl-3' : 'border-l-4 border-l-transparent'}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">{counterParty || '—'}</div>
                  <div className="text-xs text-gray-500 truncate">{t.product_name || t.subject || 'General enquiry'}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[10px] text-gray-400">{fmtAgo(t.last_message_at)}</span>
                  {unread > 0 ? <Badge className="bg-[#ee5e23] text-white text-[10px] px-1.5 py-0 leading-4">{unread}</Badge> : null}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <p className="text-xs text-gray-600 truncate flex-1">{t.last_message_preview || '—'}</p>
                <StatusBadge status={t.status} />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const MessageBubble = ({ msg, role }) => {
  const isMine =
    (role === 'customer' && msg.sender_type === 'customer') ||
    (role === 'vendor' && msg.sender_type === 'vendor');
  const align = isMine ? 'items-end' : 'items-start';
  const bubble = isMine
    ? 'bg-[#2c54b2] text-white'
    : 'bg-white border border-gray-200 text-gray-900';
  return (
    <div className={`flex flex-col ${align}`}>
      <div className="text-[11px] text-gray-500 mb-0.5">
        {isMine ? 'You' : msg.sender_name} · {fmtAgo(msg.created_at)}
        {msg.type === 'accept' && <span className="ml-1 text-emerald-600 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> Accepted</span>}
        {msg.type === 'reject' && <span className="ml-1 text-red-600 inline-flex items-center gap-0.5"><XCircle className="w-3 h-3" /> Rejected</span>}
        {msg.type === 'revision_request' && <span className="ml-1 text-amber-600 inline-flex items-center gap-0.5"><RefreshCcw className="w-3 h-3" /> Revision request</span>}
        {msg.type === 'enquiry' && <span className="ml-1 text-blue-600 inline-flex items-center gap-0.5"><BadgeCheck className="w-3 h-3" /> Enquiry</span>}
      </div>
      <div className={`rounded-2xl px-3 py-2 max-w-[80%] text-sm whitespace-pre-wrap ${bubble}`} data-testid={`msg-${msg.id}`}>
        {msg.body}
        {msg.quotation ? <QuotationCard q={msg.quotation} /> : null}
        <AttachmentChips attachments={msg.attachments} />
      </div>
    </div>
  );
};

const VendorQuotationComposer = ({ onSubmit, submitting, role, threadId }) => {
  const [q, setQ] = useState({ price: '', moq: '', unit: '', validity: '', terms: '' });
  const [note, setNote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const send = (e) => {
    e.preventDefault();
    if (!q.price) return;
    onSubmit({
      type: 'quotation',
      body: note,
      quotation: { ...q, price: Number(q.price), attachments: attachments.length ? attachments : undefined },
    });
    setQ({ price: '', moq: '', unit: '', validity: '', terms: '' });
    setNote('');
    setAttachments([]);
  };
  return (
    <form onSubmit={send} className="border-t border-gray-200 p-3 space-y-2 bg-amber-50/40" data-testid="vendor-quote-composer">
      <p className="text-xs font-semibold uppercase tracking-wider text-amber-900">Send Quotation</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Input data-testid="quote-price" required type="number" placeholder="Price ₹" value={q.price} onChange={(e) => setQ({ ...q, price: e.target.value })} />
        <Input data-testid="quote-moq" placeholder="MOQ" value={q.moq} onChange={(e) => setQ({ ...q, moq: e.target.value })} />
        <Input data-testid="quote-unit" placeholder="Unit (Kg, Pcs)" value={q.unit} onChange={(e) => setQ({ ...q, unit: e.target.value })} />
        <Input data-testid="quote-validity" type="date" value={q.validity} onChange={(e) => setQ({ ...q, validity: e.target.value })} />
        <Input data-testid="quote-terms" placeholder="Terms" value={q.terms} onChange={(e) => setQ({ ...q, terms: e.target.value })} />
      </div>
      <Textarea data-testid="quote-note" placeholder="Cover note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <AttachmentPicker role={role} threadId={threadId} disabled={submitting} attachments={attachments} setAttachments={setAttachments} testId="quote-attach-btn" />
        <Button type="submit" disabled={submitting} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="send-quotation-btn">
          <Send className="w-4 h-4 mr-1" /> Send Quotation
        </Button>
      </div>
    </form>
  );
};

const TextComposer = ({ onSubmit, submitting, placeholder, lastVendorIsQuotation, onAccept, onRevise, onReject, role, threadId }) => {
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const send = (e) => {
    e.preventDefault();
    if (!body.trim() && attachments.length === 0) return;
    onSubmit({
      type: 'text',
      body: body.trim(),
      attachments: attachments.length ? attachments : undefined,
    });
    setBody('');
    setAttachments([]);
  };
  return (
    <form onSubmit={send} className="border-t border-gray-200 p-3 space-y-2 bg-white" data-testid={`${role}-text-composer`}>
      {role === 'customer' && lastVendorIsQuotation && (
        <div className="flex flex-wrap gap-2" data-testid="customer-quote-actions">
          <Button type="button" onClick={onAccept} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="accept-quote-btn">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Accept Quote
          </Button>
          <Button type="button" onClick={onReject} disabled={submitting} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="reject-quote-btn">
            <XCircle className="w-4 h-4 mr-1" /> Reject Quote
          </Button>
          <Button type="button" onClick={onRevise} disabled={submitting} variant="outline" data-testid="request-revision-btn">
            <RefreshCcw className="w-4 h-4 mr-1" /> Request Revision
          </Button>
        </div>
      )}
      <div className="flex gap-2">
        <Textarea data-testid={`${role}-msg-input`} rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={placeholder} className="flex-1" />
        <Button type="submit" disabled={submitting || (!body.trim() && attachments.length === 0)} className="bg-[#2c54b2] hover:bg-[#1e3d80] text-white" data-testid={`${role}-send-text-btn`}>
          <Send className="w-4 h-4" />
        </Button>
      </div>
      <AttachmentPicker role={role} threadId={threadId} disabled={submitting} attachments={attachments} setAttachments={setAttachments} testId={`${role}-attach-btn`} />
    </form>
  );
};

const QuoteInbox = ({ role }) => {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(params.get('thread') || null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reloadThreads = () => inboxApi.list(role).then(setThreads).catch(() => setThreads([]));

  useEffect(() => {
    setLoading(true);
    inboxApi.list(role).then(setThreads).catch(() => setThreads([])).finally(() => setLoading(false));
  }, [role]);

  useEffect(() => {
    if (!activeId) { setActiveThread(null); setMessages([]); return; }
    setParams({ thread: activeId }, { replace: true });
    inboxApi.get(role, activeId).then((d) => {
      setActiveThread(d.thread); setMessages(d.messages || []);
      inboxApi.markRead(role, activeId).then(reloadThreads).catch(() => {});
    }).catch((e) => {
      toast({ title: 'Failed to load conversation', description: e?.response?.data?.detail || '', variant: 'destructive' });
      setActiveId(null);
    });
  }, [activeId, role]);

  const lastVendorIsQuotation = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].sender_type === 'vendor') return messages[i].type === 'quotation';
    }
    return false;
  }, [messages]);

  const send = async (payload) => {
    if (!activeId) return;
    setSubmitting(true);
    try {
      await inboxApi.postMessage(role, activeId, payload);
      const d = await inboxApi.get(role, activeId);
      setActiveThread(d.thread); setMessages(d.messages || []);
      reloadThreads();
    } catch (e) {
      toast({ title: 'Could not send', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-72px)] bg-gray-50" data-testid={`${role}-quote-inbox`}>
      {/* LEFT — thread list */}
      <aside className={`bg-white border-r border-gray-200 overflow-y-auto ${activeId ? 'hidden md:block' : ''}`}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><InboxIcon className="w-4 h-4" /> {role === 'vendor' ? 'Buyer Enquiries' : 'My Enquiries'}</h2>
          <span className="text-[11px] text-gray-500">{threads.length} thread{threads.length === 1 ? '' : 's'}</span>
        </div>
        {loading ? <div className="p-6 text-sm text-gray-400">Loading…</div> : <ThreadList threads={threads} role={role} activeId={activeId} onSelect={(t) => setActiveId(t.id)} />}
      </aside>

      {/* RIGHT — conversation */}
      <section className={`flex flex-col bg-gray-50 ${activeId ? '' : 'hidden md:flex'}`}>
        {!activeThread ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500" data-testid="inbox-no-selection">
            Select a conversation from the left.
          </div>
        ) : (
          <>
            <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
              <button type="button" onClick={() => setActiveId(null)} className="md:hidden text-gray-600" data-testid="inbox-back-btn"><ArrowLeft className="w-5 h-5" /></button>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900 truncate">{role === 'customer' ? activeThread.vendor_name : activeThread.customer_name}</div>
                <div className="text-xs text-gray-500 truncate">{activeThread.product_name || activeThread.subject}</div>
              </div>
              <StatusBadge status={activeThread.status} />
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="inbox-messages">
              {messages.map((m) => <MessageBubble key={m.id} msg={m} role={role} />)}
              {messages.length === 0 && <div className="text-center text-sm text-gray-400 mt-12">No messages yet.</div>}
            </div>
            {activeThread.status === 'accepted' ? (
              <div className="border-t border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 text-center" data-testid="thread-accepted-notice">
                <CheckCircle2 className="w-4 h-4 inline mr-1" /> Quotation accepted. The vendor will reach out to finalise the order.
                {role === 'customer' && (
                  <div className="mt-1 text-[11px] text-emerald-700/80">
                    This conversation is closed. Send a new enquiry to start a fresh negotiation.
                  </div>
                )}
              </div>
            ) : activeThread.status === 'rejected' ? (
              <div className="border-t border-red-200 bg-red-50 p-3 text-sm text-red-800 text-center" data-testid="thread-rejected-notice">
                <XCircle className="w-4 h-4 inline mr-1" /> Quotation rejected. This conversation is closed.
                {role === 'customer' && (
                  <div className="mt-1 text-[11px] text-red-700/80">
                    Not the right fit? Post a new enquiry to try again with this or another vendor.
                  </div>
                )}
              </div>
            ) : activeThread.status === 'closed' ? (
              <div className="border-t border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 text-center" data-testid="thread-closed-notice">
                Conversation closed.
              </div>
            ) : role === 'vendor' ? (
              <>
                <VendorQuotationComposer onSubmit={send} submitting={submitting} role="vendor" threadId={activeThread.id} />
                <TextComposer onSubmit={send} submitting={submitting} placeholder="Reply to buyer…" role="vendor" threadId={activeThread.id} />
              </>
            ) : (
              <TextComposer
                onSubmit={send}
                submitting={submitting}
                placeholder="Reply to vendor…"
                lastVendorIsQuotation={lastVendorIsQuotation}
                onAccept={() => send({ type: 'accept', body: 'Accepted the quotation.' })}
                onReject={() => send({ type: 'reject', body: 'Rejected the quotation.' })}
                onRevise={() => send({ type: 'revision_request', body: 'Please send a revised quotation.' })}
                role="customer"
                threadId={activeThread.id}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default QuoteInbox;
